import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { ProductCard } from "../components/ProductCard";
import { categories, publishedProducts, waLink, waCategoryMessage, waGeneralMessage } from "../data/store";

export default function Produk() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.shortDesc.toLowerCase().includes(q)
  );

  return (
    <>
      <Seo
        title="Kategori Barang | Toko Bangunan NJA Bajawa"
        description="Lihat kategori bahan bangunan di Toko Nuansa Jaya Abadi Bajawa: bahan bangunan, cat, keramik, pipa, sanitair, listrik, dan perkakas. Cek ketersediaan via WhatsApp."
        path="/produk"
      />

      <section className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:py-16">
        <h1 className="text-3xl font-extrabold text-navy sm:text-4xl">
          Kategori barang
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
          Daftar jenis bahan bangunan yang tersedia di toko kami di Bajawa.
          Untuk harga dan stok terbaru, silakan tanya langsung melalui
          WhatsApp — ketersediaan barang dapat berubah setiap hari.
        </p>

        <div className="mt-8 max-w-md">
          <label htmlFor="category-search" className="sr-only">
            Cari kategori barang
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              id="category-search"
              data-testid="category-search-input"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari kategori, mis. cat atau pipa…"
              className="w-full rounded-full border border-slate-300 bg-white py-3 pl-12 pr-4 text-base text-slate-800 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/30"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <p data-testid="category-search-empty" className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 text-base text-slate-600">
            Kategori “{query}” tidak ditemukan. Coba kata kunci lain, atau{" "}
            <a
              href={waLink(waGeneralMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand hover:underline"
            >
              tanyakan langsung via WhatsApp
            </a>
            .
          </p>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((c) => (
              <article
                key={c.slug}
                data-testid={`product-card-${c.slug}`}
                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-md"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand/10 text-brand">
                  <c.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h2 className="mt-4 text-lg font-semibold text-navy">{c.name}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{c.shortDesc}</p>
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/produk/${c.slug}`}
                    data-testid={`product-detail-link-${c.slug}`}
                    className="inline-flex items-center gap-1 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-navy-light"
                  >
                    Detail
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <a
                    href={waLink(waCategoryMessage(c.name))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-wa-link-${c.slug}`}
                    className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
                  >
                    <MessageCircle className="h-4 w-4" aria-hidden="true" />
                    Cek ketersediaan
                  </a>
                </div>
              </article>
            ))}
          </div>
        )}

        {publishedProducts.length > 0 && (
          <>
            <h2 className="mt-12 text-2xl font-bold text-navy">Produk unggulan</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600">
              Barang pilihan di toko kami. Harga dan ketersediaan dapat berubah —
              konfirmasi langsung via WhatsApp.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {publishedProducts
                .filter((p) =>
                  [p.name, p.brand, p.spec]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                    .includes(q)
                )
                .map((p) => (
                  <ProductCard key={p.name} product={p} />
                ))}
            </div>
          </>
        )}

        <div className="mt-12 rounded-2xl bg-navy p-6 text-white shadow-lg sm:p-8">
          <h2 className="text-lg font-semibold sm:text-xl">
            Tidak menemukan barang yang dicari?
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-white/80">
            Daftar kategori di atas masih dapat berkembang. Kirim kebutuhan Anda
            melalui WhatsApp dan kami akan membantu mengecek ketersediaannya.
          </p>
          <a
            href={waLink(waGeneralMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="catalog-whatsapp-cta"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-wa-dark"
          >
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
            Tanya via WhatsApp
          </a>
        </div>
      </section>
    </>
  );
}
